// src/pages/Home.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import BASE_URL, { DAJC, SITE_URL } from "../config";
import {
  getTitle,
  getImage,
  getExcerpt,
  formatDate,
  localePath,
} from "../utils/content";
import BrandLogo from "../components/BrandLogo";
import Seo, { absoluteUrl } from "../components/Seo";
import logoCcma from "../assets/logo-ccma-fr.png";

/** Reusable card-level image fallback */
function CardImageFallback({ title = "" }) {
  const initials = title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  return (
    <div className="card-article__media-fallback" aria-hidden="true">
      <svg
        viewBox="0 0 320 200"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id="homeCardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--ink, #0d1b2a)" stopOpacity="1" />
            <stop offset="100%" stopColor="#1a2e45" stopOpacity="1" />
          </linearGradient>
        </defs>
        <rect width="320" height="200" fill="url(#homeCardGrad)" />

        <g transform="translate(95, 25)" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" fill="none" opacity="0.16">
          <line x1="65" y1="0" x2="65" y2="130" />
          <line x1="20" y1="150" x2="110" y2="150" />
          <line x1="35" y1="28" x2="95" y2="28" />
          <path d="M35 28 l-22 46 a22 22 0 0 0 44 0 l-22-46z" />
          <path d="M95 28 l-22 46 a22 22 0 0 0 44 0 l-22-46z" />
        </g>

        <line x1="0" y1="0" x2="28" y2="0" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" opacity="0.4" />
        <line x1="0" y1="0" x2="0" y2="28" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" opacity="0.4" />
        <line x1="320" y1="200" x2="292" y2="200" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" opacity="0.4" />
        <line x1="320" y1="200" x2="320" y2="172" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" opacity="0.4" />

        {initials ? (
          <text
            x="160"
            y="110"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="40"
            fontFamily="Georgia, serif"
            letterSpacing="4"
            opacity="0.85"
          >
            {initials}
          </text>
        ) : (
          <text
            x="160"
            y="110"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="46"
            opacity="0.6"
          >
            ⚖
          </text>
        )}
      </svg>
    </div>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Fiche d'identité du site pour les moteurs de recherche
  const siteJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: t("brand.full"),
      alternateName: DAJC.NAME,
      url: absoluteUrl("", lang),
      logo: `${SITE_URL}${logoCcma}`,
      email: DAJC.EMAIL,
      telephone: t("contact.phone"),
      address: {
        "@type": "PostalAddress",
        streetAddress: t("contact.address"),
        addressLocality: "Alger",
        addressCountry: "DZ",
      },
      parentOrganization: {
        "@type": "Organization",
        name: "Chambre Algérienne de Commerce et d'Industrie",
        url: "https://www.caci.dz",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: t("brand.full"),
      url: SITE_URL,
      inLanguage: lang,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?keyword={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];
  const [articles, setArticles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios
        .get(`${BASE_URL}/api/categoryportail/edit/${DAJC.CATEGORY_ID}`, {
          params: { per_page: 6, page: 1 },
        })
        .then((r) => r.data?.articles?.data || [])
        .catch(() => []),
      axios
        .get(`${BASE_URL}/api/serviceportail/edit/${DAJC.SERVICE_ID}`, {
          params: { per_page: 4, page: 1 },
        })
        .then((r) => r.data?.pages?.data || [])
        .catch(() => []),
    ]).then(([a, p]) => {
      if (cancelled) return;
      setArticles(a);
      setPages(p);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <Seo path="" description={t("hero.subtitle")} jsonLd={siteJsonLd} />

      {/* HERO */}
      <section className="hero">
        <div className="hero-circle-2" />
        <div className="container">
          <div className="hero__inner">
            <div>
              <div className="hero__breadcrumb">
                <span>{t("brand.parent")}</span>
                <span>·</span>
                <span style={{ color: "var(--gold-soft)" }}>{t("brand.short")}</span>
              </div>
              <h1 className="hero__title">
                {t("hero.title").split(" ").slice(0, -2).join(" ")}{" "}
                <em>{t("hero.title").split(" ").slice(-2).join(" ")}</em>
              </h1>
              <p className="hero__subtitle">{t("hero.subtitle")}</p>
              <div className="hero__ctas">
                <Link to={localePath(lang, "page/2")} className="btn btn-primary">
                  {t("hero.cta_primary")}
                </Link>
                <Link to={localePath(lang, "page/12")} className="btn btn-ghost">
                  {t("hero.cta_secondary")}
                </Link>
              </div>
            </div>

            <div className="hero__visual">
              <div className="hero__seal">
                <span className="hero__orbit" aria-hidden="true" />
                <span className="hero__orbit hero__orbit--inner" aria-hidden="true" />
                <div className="hero__seal-float">
                  <BrandLogo lang={lang} large decorative className="hero__seal-logo" />
                  <span className="hero__seal-shine" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="mission">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">{t("mission.eyebrow")}</div>
            <h2>{t("mission.title")}</h2>
            <p>{t("mission.intro")}</p>
          </div>

          <div className="mission-grid">
            {[
              { key: "commissions", icon: <UsersIcon /> },
              { key: "studies",     icon: <BookIcon /> },
              { key: "arbitration", icon: <GavelIcon /> },
              { key: "ccma",        icon: <BuildingIcon /> },
            ].map((it, idx) => (
              <div className="mission-card" key={it.key}>
                <div className="mission-card__num">0{idx + 1}</div>
                <div className="mission-card__icon">{it.icon}</div>
                <h3>{t(`mission.items.${it.key}.title`)}</h3>
                <p>{t(`mission.items.${it.key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CCMA SECTION */}
      <section className="ccma">
        <div className="container">
          <div className="ccma__inner">
            <div>
              <div className="eyebrow">{t("ccma.eyebrow")}</div>
              <h2>
                {t("ccma.title").split(",").map((s, i, arr) =>
                  i === arr.length - 1
                    ? <em key={i}>{s}</em>
                    : <React.Fragment key={i}>{s},<br /></React.Fragment>
                )}
              </h2>
              <p style={{ marginBottom: 36 }}>{t("ccma.intro")}</p>
              <Link to={localePath(lang, "page/12")} className="btn btn-gold">
                {t("ccma.explore")}
              </Link>
            </div>

            <div className="ccma-missions">
              <h3>{t("ccma.missions_title")}</h3>
              <ol>
                <li>{t("ccma.m1")}</li>
                <li>{t("ccma.m2")}</li>
                <li>{t("ccma.m3")}</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          {/* Responsive section head: stacks on mobile */}
          <div className="home-section-head">
            <div>
              <div className="eyebrow">{t("articles.eyebrow")}</div>
              <h2 style={{ marginBottom: 8 }}>{t("articles.title")}</h2>
              <p style={{ color: "var(--muted)" }}>{t("articles.subtitle")}</p>
            </div>
            <Link to={localePath(lang, "publications")} className="btn btn-ghost home-section-head__cta">
              {t("articles.view_all")}
            </Link>
          </div>

          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : articles.length === 0 ? (
            <div className="empty"><h3>{t("search.noResults")}</h3></div>
          ) : (
            <div className="cards-grid">
              {articles.slice(0, 6).map((art) => (
                <ArticleCard key={art.id} article={art} lang={lang} t={t} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PAGES / RESOURCES */}
      <section style={{ background: "var(--paper)" }}>
        <div className="container">
          <div className="home-section-head">
            <div>
              <div className="eyebrow">{t("pages.eyebrow")}</div>
              <h2 style={{ marginBottom: 8 }}>{t("pages.title")}</h2>
              <p style={{ color: "var(--muted)" }}>{t("pages.subtitle")}</p>
            </div>
            <Link to={localePath(lang, "ressources")} className="btn btn-ghost home-section-head__cta">
              {t("pages.view_all")}
            </Link>
          </div>

          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : (
            <div className="cards-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              {pages.slice(0, 4).map((p) => (
                <Link
                  key={p.id}
                  to={localePath(lang, `page/${p.id}/${p.slug || ""}`)}
                  className="card-article"
                  style={{ textDecoration: "none" }}
                >
                  <div className="card-article__body" style={{ padding: 32 }}>
                    <div className="card-article__date" style={{ color: "var(--gold-deep)" }}>
                      {t("pages.consult")}
                    </div>
                    <h3 className="card-article__title" style={{ fontSize: 20, minHeight: 60 }}>
                      {getTitle(p, lang, 90)}
                    </h3>
                    <div className="card-article__more">{t("articles.read_more")}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/* ---------- Article Card with image fallback ---------- */
function ArticleCard({ article, lang, t }) {
  const [imgError, setImgError] = useState(false);
  const img = getImage(article, lang);
  const title = getTitle(article, lang, 80);
  const showFallback = !img || imgError;

  return (
    <article className="card-article">
      <div className="card-article__media">
        {!showFallback ? (
          <img
            src={img}
            alt={title}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <CardImageFallback title={title} />
        )}
      </div>
      <div className="card-article__body">
        <div className="card-article__date">
          {formatDate(article.starts_at || article.created_at, lang, t)}
        </div>
        <h3 className="card-article__title">
          <Link to={localePath(lang, `article/${article.id}/${article.slug || ""}`)}>
            {title}
          </Link>
        </h3>
        <p className="card-article__excerpt">{getExcerpt(article, lang, 22)}</p>
        <Link
          to={localePath(lang, `article/${article.id}/${article.slug || ""}`)}
          className="card-article__more"
        >
          {t("articles.read_more")}
        </Link>
      </div>
    </article>
  );
}

/* ---------- Inline icons ---------- */
function GavelIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4l6 6M11 7l6 6M9 9l-5 5a2 2 0 0 0 0 3l1 1a2 2 0 0 0 3 0l5-5M3 21h12" />
    </svg>
  );
}
function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 0-2 2V5zM19 17H6" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" transform="translate(0.5,0)" />
    </svg>
  );
}
function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M14 9h.01M14 13h.01M14 17h.01" />
    </svg>
  );
}